import { Injectable } from "@angular/core";
import { Subject, Subscription, Observable } from "rxjs";

import {
  BfxBlotterAction,
  BfxBlotterContextMenuItem,
  ContextMenuItemState,
} from "shared/cubiq-services";
import { BfxBaseBlotter } from "./bfx-base-blotter";

export enum BfxLadderBlotterContextMenuEventType {
  MENU_SHOW,
  MENU_HIDE,

  MENU_UPDATE,
  MENU_HIDE_ALL,
}

export const DELETED_SELECTED_ITEM = Symbol("DELETED_SELECTED_ITEM");

export class BfxLadderBlotterContextMenuEvent<T> {
  constructor(
    public eventType: BfxLadderBlotterContextMenuEventType,
    public ladderBlotter: BfxBaseBlotter,
    public x?: number,
    public y?: number,
    public enabledActionIds: Array<BfxBlotterAction> = [],
    public actionDecorators: any = {},
    public menuItems: Array<BfxBlotterContextMenuItem<T>> = [],
  ) {}
}

export class BfxBlotterActionEvent {
  constructor(
    public action: BfxBlotterAction,
    public ladderBlotter: BfxBaseBlotter,
    public selectedIds: Array<string>,
    public selectedItems: Array<any>,
    public itemState?: ContextMenuItemState,
    public hide?: boolean,
    public menuBoundingClientRect?: any,
    public sourceEvent?: any,
    public body?: Element,
  ) {}
}

@Injectable()
export class BfxBlotterActionSubject extends Subject<BfxBlotterActionEvent> {
  // this is an injector wrapper
  // note considered an Inject()-able tokens instead but that's hard to express the split between
  // shared between Orders+Inbox+Ladder and other singleton usages.
}

export interface BfxBlotterActionSubject {
  next(value: BfxBlotterActionEvent): void;
  error(err: any): void;
  complete(): void;
  unsubscribe(): void;
  subscribe(...args: any[]): Subscription;
  pipe(...operators: any[]): Observable<any>;
}

/**
 * Override to implements specifics for specific blotter
 */
export abstract class BfxBaseBlotterContextMenuService<T> {
  public showMenuSubject: Subject<BfxLadderBlotterContextMenuEvent<T>> = new Subject<
    BfxLadderBlotterContextMenuEvent<T>
  >();

  public menuData = new WeakMap<
    object,
    {
      pageX: number;
      pageY: number;
      selectedIds: Array<string>;
      selectedItems?: Array<object> | null;
    }
  >();

  constructor(public actionSubject: BfxBlotterActionSubject) {}

  /**
   * Override this
   * Insepect the items to determing what actions are enabled for the data
   * @param items
   * @param selectedIds
   */
  protected abstract getEnabledActions(
    items: Array<any>,
    selectedIds: Array<string> | Array<number>,
  ): Array<BfxBlotterAction>;

  /**
   * Override this
   * There is a facility to add decorators to the contect menu items, see Quote implementation where rates are added the the accept item labels
   * @param items
   */
  /*  protected abstract getActionDecorators(items: Array<any>): any; */

  /**
   * Override this
   * You may want to add extra listeners to the blotter to perform actions, quotes blotter adds a double click to pick up an order
   * Orders one will probalbly need to add a double click to show order details
   * @param ladderBlotter
   */
  /* protected abstract prepareActions(ladderBlotter: any): void; */

  /**
   * Override this
   * This is the provider for the context menu,should return an array of BfxBlotterContextMenuItem<any>'s
   * Some menus items may need to be hidden depending on system configs etc
   */
  public abstract getMenuItems(): Array<BfxBlotterContextMenuItem<T>>;

  /**
   * override with implementation details of menu
   *
   * _must_ only be called from doMenu() which acts as guard keeping menuData correct
   * @param blotter
   * @param pageX
   * @param pageY
   * @param selectedIds
   * @param selectedItems optional but must be a parallel array with selectionIds
   */
  protected abstract buildMenu(
    blotter: object,
    pageX: number,
    pageY: number,
    selectedIds: Array<string>,
    selectedItems?: Array<object> | null,
  ): void;

  /**
   *
   * @param blotter
   * @param pageX
   * @param pageY
   * @param selectedIds
   * @param selectedItems optional but must be a parallel array with selectionIds
   */
  public doMenu(
    blotter: object,
    pageX: number,
    pageY: number,
    selectedIds: Array<string>,
    selectedItems?: Array<object> | null,
  ): void {
    this.buildMenu(
      blotter,
      pageX,
      pageY,
      [...selectedIds],
      Array.isArray(selectedItems) ? [...selectedItems] : null,
    );

    this.menuData.set(blotter, {
      pageX: pageX,
      pageY: pageY,
      selectedIds: selectedIds,
      selectedItems: selectedItems,
    });
  }

  /**
   * this is expensive, please guard with a leading audit
   * note does not imply previous selection - no ops will be filtered here
   *
   * @param blotter
   * @param updatedModelItems
   * @param deletedModelIds
   */
  public updateMenu(
    blotter: BfxBaseBlotter,
    updatedModelItems: Map<string, object | typeof DELETED_SELECTED_ITEM>,
  ) {
    if (this.menuData.has(blotter)) {
      const { pageX, pageY, selectedIds, selectedItems } = this.menuData.get(blotter);
      let touched = false;
      let hasSelectedItems = Array.isArray(selectedItems);

      selectedIds.forEach((id, index, selectedIds) => {
        if (updatedModelItems.has(id)) {
          const item = updatedModelItems.get(id);

          if (item == DELETED_SELECTED_ITEM) {
            selectedIds.splice(index, 1);

            if (hasSelectedItems) {
              selectedItems.splice(index, 1);
            }
          } else if (hasSelectedItems) {
            selectedItems[index] = item;
          }

          touched = true;
        }
      });

      if (touched && selectedIds.length > 0) {
        this.doMenu(blotter, pageX, pageY, selectedIds, selectedItems);
      } else if (touched && selectedIds.length == 0) {
        this.hideMenu(blotter);
      }
    } else {
      this.hideMenu(blotter);
    }
  }

  public showMenuAt(
    ladderBlotter: any,
    x: number,
    y: number,
    enabledActionIds: Array<BfxBlotterAction>,
    actionDecorators: any = {},
  ): void {
    this.showMenuSubject.next(
      new BfxLadderBlotterContextMenuEvent<T>(
        BfxLadderBlotterContextMenuEventType.MENU_SHOW,
        ladderBlotter,
        x,
        y,
        enabledActionIds,
        actionDecorators,
        this.getMenuItems(),
      ),
    );
  }

  public hideMenu(ladderBlotter: BfxBaseBlotter): void {
    this.showMenuSubject.next(
      new BfxLadderBlotterContextMenuEvent<T>(
        BfxLadderBlotterContextMenuEventType.MENU_HIDE,
        ladderBlotter,
      ),
    );
    this.menuData.delete(ladderBlotter);
  }

  public fireActionEvent(
    actionId: BfxBlotterAction,
    ladderBlotter: any,
    selectedIds?: Array<string>,
    selectedItems?: Array<any>,
    itemState?: ContextMenuItemState,
    hide?: boolean,
    menuBoundingClientRect?: any,
    actionEvent?: any,
  ): void {
    let event: BfxBlotterActionEvent = new BfxBlotterActionEvent(
      actionId,
      ladderBlotter,
      this.getSelectedIds(selectedIds, ladderBlotter),
      this.getSelectedItems(selectedItems, ladderBlotter),
      itemState,
      hide,
      menuBoundingClientRect,
      actionEvent,
    );
    this.actionSubject.next(event);
  }

  protected getSelectedItems(selectedItems: any[], ladderBlotter: any): any[] {
    if (Array.isArray(selectedItems) && selectedItems.length > 0) {
      return selectedItems;
    }

    if (this.menuData.has(ladderBlotter)) {
      let { selectedItems } = this.menuData.get(ladderBlotter);
      if (Array.isArray(selectedItems) && selectedItems.length > 0) {
        return selectedItems;
      }
    }

    return [];
  }

  protected setSelectedItems(selectedItems: any[], ladderBlotter: any): void {
    if (this.menuData.has(ladderBlotter)) {
      this.menuData.get(ladderBlotter).selectedItems = selectedItems;
    }
  }

  protected getSelectedIds(selectedIds: string[], ladderBlotter: any): string[] {
    if (Array.isArray(selectedIds) && selectedIds.length > 0) {
      return selectedIds;
    }

    if (this.menuData.has(ladderBlotter)) {
      let { selectedIds } = this.menuData.get(ladderBlotter);
      if (Array.isArray(selectedIds) && selectedIds.length > 0) {
        return selectedIds;
      }
    }

    return [];
  }

  /**
   * Reduces the input list of MenuItems, trimming all SEPARATORS from the start and end
   * of the list, removing items that are not available, and removing duplicate adjacent
   * SEPARATOR items
   * @param menuItems
   * @returns
   */
  protected reduceVisibleMenuItems<U extends BfxBlotterContextMenuItem<any>>(
    menuItems: U[],
    selectedIds: Array<string> | null = null,
    selectedItems: Array<object> | null = null,
  ): U[] {
    const retItems = [];
    if (menuItems?.length > 0) {
      // Remove not available items
      menuItems = menuItems.filter((menuItem) => menuItem?.state?.available);
      if (menuItems.length > 0) {
        let firstIndex = 0;
        while (
          // Trim separators from the top
          menuItems[firstIndex].actionId === BfxBlotterAction.SEPARATOR &&
          firstIndex < menuItems.length
        ) {
          firstIndex++;
        }

        let lastIndex = menuItems.length - 1;
        while (
          // Trim separators form the bottom
          menuItems[lastIndex].actionId === BfxBlotterAction.SEPARATOR &&
          lastIndex > firstIndex
        ) {
          lastIndex--;
        }

        let lastAction = null;
        for (let i = firstIndex; i <= lastIndex; i++) {
          // REmove duplicate adjacent SEPARATORS
          const currentAction = menuItems[i].actionId;
          if (
            lastAction !== BfxBlotterAction.SEPARATOR ||
            currentAction !== BfxBlotterAction.SEPARATOR
          ) {
            retItems.push(menuItems[i]);
            lastAction = currentAction;
          }
        }
      }
    }
    return retItems;
  }
}
